import React, { useState, useRef, useEffect } from "react";
import UserAccountHeader from "./UserAccountHeader";
import { HiSearch } from "react-icons/hi";
import Header from "../components/Header";
import { motion, AnimatePresence } from "framer-motion";
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Graph from "./Graph";
import { ChevronRight } from 'lucide-react';
import DashboardData from "./DashboardData";
import AdminSideBar from "../components/AdminSideBar";
import ReportDropdown2 from "../components/ReportDropdown2";
import Footer from "../components/Footer";
import Reports from "../../pages/Reports1";

const Dropdown1 = ({ label, items, type, isProjectDropdown, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCircles, setSelectedCircles] = useState({});
  
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  const toggleCircle = (item) => {
      setSelectedCircles((prev) => ({
          ...prev,
          [item]: !prev[item],
      }));
  };

  const handleSelect = (item) => {
      setSelectedItems(prev => {
          if (prev.includes(item)) {
              return prev.filter(i => i !== item);
          } else {
              return [...prev, item];
          }
      });
      
      if (typeof onChange === "function") {
          onChange(type, item);
      }
  };

  useEffect(() => {
      const content = contentRef.current;
      if (!content) return;

      const updateThumbHeight = () => {
          const { scrollHeight, clientHeight } = content;
          const visibleRatio = clientHeight / scrollHeight;
          setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
      };

      updateThumbHeight();
      window.addEventListener("resize", updateThumbHeight);
      return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, items]);

  const handleScroll = () => {
      const content = contentRef.current;
      if (!content) return;

      const { scrollTop, scrollHeight, clientHeight } = content;
      const maxScrollTop = scrollHeight - clientHeight;
      const scrollRatio = scrollTop / maxScrollTop;
      const trackHeight = content.clientHeight - thumbHeight;
      setScrollTop(scrollRatio * trackHeight);
  };

  const handleThumbMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);
      setStartY(e.clientY);
      setStartScrollTop(contentRef.current.scrollTop);

      document.addEventListener("mousemove", handleThumbMouseMove);
      document.addEventListener("mouseup", handleThumbMouseUp);
      document.body.style.cursor = "grabbing";
  };

  const handleThumbMouseMove = (e) => {
      if (!isDragging) return;

      const deltaY = e.clientY - startY;
      const content = contentRef.current;
      const { scrollHeight, clientHeight } = content;
      const maxScrollTop = scrollHeight - clientHeight;
      const trackHeight = clientHeight - thumbHeight;

      const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
      const newScrollTop = Math.min(
          Math.max(0, startScrollTop + scrollDelta),
          maxScrollTop
      );

      content.scrollTop = newScrollTop;
  };

  const handleThumbMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleThumbMouseMove);
      document.removeEventListener("mouseup", handleThumbMouseUp);
      document.body.style.cursor = "";
  };

  const handleTrackClick = (e) => {
      const content = contentRef.current;
      if (!content || e.target === thumbRef.current) return;

      const track = e.currentTarget;
      const trackRect = track.getBoundingClientRect();
      const clickPosition = e.clientY - trackRect.top;
      const thumbCenter = thumbHeight / 2;
      const scrollRatio =
          (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

      const { scrollHeight, clientHeight } = content;
      const maxScrollTop = scrollHeight - clientHeight;
      content.scrollTop = scrollRatio * maxScrollTop;
  };

  const handleButtonScroll = (direction) => {
      if (contentRef.current) {
          contentRef.current.scrollBy({
              top: direction === 'up' ? -50 : 50,
              behavior: 'smooth'
          });
      }
  };

  return (
      <div className="flex flex-col w-full relative">
          <span className="text-[6px] md:text-[20px] md:mb-3 font-extrabold">
              {isProjectDropdown ? "" : label || "LANGUAGE"}
          </span>
          <div className="flex relative z-20 w-fit">
              <button
                  onClick={() => {
                      if (isProjectDropdown && selectedItems.includes("Funding")) {
                          window.location.href = "/funding";
                      } else {
                          setIsOpen(prev => !prev);
                      }
                  }}
                  className={`xl:w-[160px] lg:w-[140px] md:w-[55px] w-[50px] h-[15px] md:h-[40px] bg-[#2E453D] text-white font-medium flex items-center px-1 rounded-l-[5px]`}
              >
                  <span className="md:text-[18px] md:px-2 text-[4px] md:mt-0 mt-[2px] truncate">
                      {selectedItems.length > 0 ? selectedItems[0] : "-"}
                  </span>
              </button>

              <div
                  className="w-[15px] h-[15px] md:h-[40px] md:w-[40px] bg-[#FDBB26] text-white flex justify-center items-center rounded-r-[5px] -ml-2 z-30 cursor-pointer"
                  onClick={() => setIsOpen(prev => !prev)}
              >
                  <img
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745014174/Expand_down_3_ggzyur.png"
                      alt="expand"
                      className="w-2 h-[10px] md:w-6 md:h-[24px]"
                  />
              </div>
          </div>

          <AnimatePresence>
              {isOpen && (
                  <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full mt-1 left-0 z-[100] md:w-[220px] w-[70px] border-[1px] border-[#979797]/80 bg-white rounded-[1px] shadow-lg p-2"
                  >
                      <div className="w-full flex justify-end py-1">
                          <button 
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handleButtonScroll('up');
                              }}
                              className="p-1"
                          >
                              <img 
                                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png" 
                                  alt="Scroll up" 
                                  className="w-3 h-3"
                              />
                          </button>
                      </div>

                      <div className="custom-scroll-area">
                          <ul
                              className="custom-scroll-content"
                              ref={contentRef}
                              onScroll={handleScroll}
                          >
                          <li className="flex items-center justify-between px-2 py-1 mb-2">
                              <span className="text-[6px] md:text-[20px] font-semibold">
                                  {isProjectDropdown ? "Purpose" : label || "Language"}
                              </span>
                              <img
                                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750034880/Export_1_vurzim.png"
                                  alt="icon"
                                  className="md:w-[30px] md:h-[30px] h-[7px] w-[7px] cursor-pointer"
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      setIsOpen(false);
                                  }}
                              />
                          </li>
                              
                              {items && items.map((item, index) => (
                                  <li
                                      key={index}
                                      className={`flex items-center justify-between md:px-3 px-1 md:py-2 py-1 md:mt-1 rounded-md hover:bg-gray-100 cursor-pointer ${
                                          selectedItems.includes(item) ? "bg-[#E7EBEB]" : ""
                                      }`}
                                      onClick={() => handleSelect(item)}
                                  >
                                      <span className="text-black text-[4px] md:text-[18px]">
                                          {item}
                                      </span>
                                      <div
                                          className={`w-2 h-2 md:w-8 md:h-8 rounded-full ${
                                              selectedItems.includes(item) ? "bg-[#152C24]" : "bg-gray-200"
                                          }`}
                                      ></div>
                                  </li>
                              ))}
                          </ul>
                          <div className="custom-scroll-track" onClick={handleTrackClick}>
                              <div
                                  ref={thumbRef}
                                  className="custom-scroll-thumb"
                                  style={{
                                      height: `${thumbHeight}px`,
                                      top: `${scrollTop}px`,
                                      cursor: isDragging ? "grabbing" : "grab",
                                  }}
                                  onMouseDown={handleThumbMouseDown}
                              ></div>
                          </div>
                      </div>

                      <div className="w-full flex justify-end py-1">
                          <button 
                              onClick={(e) => {
                                  e.stopPropagation();
                                  handleButtonScroll('down');
                              }}
                              className="p-1"
                          >
                              <img 
                                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png" 
                                  alt="Scroll down" 
                                  className="w-3 h-3"
                              />
                          </button>
                      </div>
                  </motion.div>
              )}
          </AnimatePresence>
          <p className="md:text-[14px] text-[4px] font-normal mt-1 text-black">
          Click to select individual  <br/>country reports
          </p>

          <style jsx>{`
              .custom-scroll-area {
                  position: relative;
                  max-height: 200px;
                  display: flex;
                  overflow: hidden;
              }

              .custom-scroll-content {
                  flex: 1;
                  overflow-y: scroll;
                  scrollbar-width: none;
                  -ms-overflow-style: none;
                  padding-right: 4px;
              }

              .custom-scroll-content::-webkit-scrollbar {
                  display: none;
              }

              .custom-scroll-track {
                  width: 14px;
                  background: #e7ebeb;
                  border-radius: 3px;
                  position: relative;
                  margin-left: 4px;
                  cursor: pointer;
              }

              .custom-scroll-thumb {
                  width: 100%;
                  background: #979797;
                  border-radius: 3px;
                  position: absolute;
                  right: 0;
                  transition: background 0.2s;
              }

              .custom-scroll-thumb:hover {
                  background: #444;
              }

              @media (max-width: 768px) {
                  .custom-scroll-track {
                      width: 4px;
                      margin-left: 2px;
                  }
                  .custom-scroll-area {
                      position: relative;
                      max-height: 150px;
                      display: flex;
                      overflow: hidden;
                      width: 100%;
                  }
              }
          `}</style>
      </div>
  );
};
const languageOptions = [
  "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31"
 ];
 const SMMEsOptions = [
  {
    heading: "Agriculture and Agribusiness",
    items: [
      { label: "Smallholder farms", value: "SMF" },
      { label: "Agro-processing", value: "AGP" },
      { label: "Agri-input suppliers", value: "AIP" },
      { label: "Horticulture businesses", value: "HCB" },
      { label: "Poultry", value: "POF" },
      { label: "Fish farms", value: "FIF" },
      { label: "Agri-tech startups", value: "ATC" }
    ]
  },
  {
    heading: "Manufacturing and Processing",
    items: [
      { label: "Textile and garment production", value: "TGP" },
      { label: "Furniture making and carpentry", value: "FPC" },
      { label: "Metal fabrication", value: "MEF" },
      { label: "Soap and detergent production", value: "SDP" },
      { label: "Food and beverage processing", value: "FBP" }
    ]
  },
  {
    heading: "Retail and Trade",
    items: [
      { label: "Small shops (dukas/spazas)", value: "SDS" },
      { label: "Market vendors", value: "MAV" },
      { label: "Wholesalers and distributors", value: "WHD" },
      { label: "Online retail platforms", value: "ORP" }
    ]
  },
  {
    heading: "Services",
    items: [
      { label: "Transport and logistics", value: "TRL" },
      { label: "Repair services", value: "RPS" },
      { label: "Beauty and personal care", value: "BPC" },
      { label: "Laundry and dry cleaning", value: "LDC" },
      { label: "Hospitality", value: "HOS" },
      { label: "Event planning and catering", value: "EPC" }
    ]
  },
  {
    heading: "Technology and Innovation (Tech SMEs)",
    items: [
      { label: "Fintech", value: "FIT" },
      { label: "Healthtech", value: "HET" },
      { label: "Edtech", value: "EDT" },
      { label: "Software and app development", value: "SAD" },
      { label: "E-commerce and logistics tech", value: "ECL" }
    ]
  },
  {
    heading: "Construction and Real Estate",
    items: [
      { label: "Small construction firms", value: "SCF" },
      { label: "Real estate agents and developers", value: "RAD" },
      { label: "Hardware and building material suppliers", value: "HBM" }
    ]
  },
  {
    heading: "Creative and Cultural Industries",
    items: [
      { label: "Art and crafts production", value: "ACP" },
      { label: "Music and film production", value: "MFP" },
      { label: "Fashion and design businesses", value: "FDB" },
      { label: "Photography and videography services", value: "PVS" }
    ]
  },
  {
    heading: "Health and Wellness",
    items: [
      { label: "Private clinics and pharmacies", value: "PCP" },
      { label: "Fitness centers and gyms", value: "FCG" },
      { label: "Traditional medicine practitioners", value: "TMP" },
      { label: "Health product distributors", value: "HPD" }
    ]
  },
  {
    heading: "Education and Training",
    items: [
      { label: "Private schools and daycares", value: "PSD" },
      { label: "Vocational training centers", value: "VTC" },
      { label: "Tutoring services", value: "TUS" },
      { label: "Edtech startups", value: "EDS" }
    ]
  }
]
 
const Dashboards = () => {
  // Sample report data - replace with your actual data
  const [reportData, setReportData] = useState({
    enterpriseTotal: 1250,
    smmesBySector: [
      { sector: "Agriculture", count: 320 },
      { sector: "Manufacturing", count: 280 },
      { sector: "Retail", count: 450 },
      { sector: "Services", count: 200 }
    ],
    regions: [
      { name: "North", count: 400 },
      { name: "South", count: 350 },
      { name: "East", count: 300 },
      { name: "West", count: 200 }
    ],
    funding: [
      { type: "Grants", amount: 500000 },
      { type: "Loans", amount: 750000 },
      { type: "Equity", amount: 300000 }
    ]
  });
  const reportsRef = useRef(null);
  const getReportsData = () => {
    if (reportsRef.current) {
      return {
        reportData: reportsRef.current.getReportData(),
        regionChunks: reportsRef.current.getRegionChunks(),
        getTotalForAllRegions: reportsRef.current.getTotalForAllRegions,
        getCount: reportsRef.current.getCount
      };
    }
    return null;
  };
  
  const generateReportData = () => {
    const reportsData = getReportsData();
    if (!reportsData || !reportsData.reportData) return null;
  
    const { reportData, regionChunks } = reportsData;
    
    // Get all unique regions from all chunks
    const allRegions = regionChunks.flat().filter(region => region && region.trim() !== "");
    
    // Prepare the data for export
    const rows = allRegions.map(region => {
      const regionData = reportData[region] || {};
      
      return {
        "Region": region,
        "Agricultural Entrepreneurs": reportsData.getCount(region, null, 'total') || 0,
        "Land for Farming (Ha)": regionData.landSizeTotal || 0,
        "Production Cost": reportsData.getCount(region, null, 'total') || 0, // Assuming same as entrepreneurs count
        "Revenue": reportsData.getCount(region, null, 'total') || 0, // Assuming same as entrepreneurs count
        "Inventory": reportsData.getCount(region, null, 'total') || 0, // Assuming same as entrepreneurs count
        "Price (Average)": reportsData.getCount(region, null, 'total') || 0, // Assuming same as entrepreneurs count
        "Smallholder Farms (ESC)": reportsData.getCount(region, 'ESC', 'count') || 0,
        "Medium-Scale Farms (EMS)": reportsData.getCount(region, 'EMS', 'count') || 0,
        "Farmer Cooperatives (EFC)": reportsData.getCount(region, 'EFC', 'count') || 0,
        "Contract Farming (ECF)": reportsData.getCount(region, 'ECF', 'count') || 0,
        "Niche Farms (ENS)": reportsData.getCount(region, 'ENS', 'count') || 0,
        "Agri-Entrepreneurs (EAE)": reportsData.getCount(region, 'EAE', 'count') || 0,
        "Youth/Women-led (EYW)": reportsData.getCount(region, 'EYW', 'count') || 0
      };
    });
  
    // Add a totals row
    if (rows.length > 0) {
      const totals = {
        "Region": "TOTAL",
        "Agricultural Entrepreneurs": rows.reduce((sum, row) => sum + row["Agricultural Entrepreneurs"], 0),
        "Land for Farming (Ha)": rows.reduce((sum, row) => sum + row["Land for Farming (Ha)"], 0),
        "Production Cost": rows.reduce((sum, row) => sum + row["Production Cost"], 0),
        "Revenue": rows.reduce((sum, row) => sum + row["Revenue"], 0),
        "Inventory": rows.reduce((sum, row) => sum + row["Inventory"], 0),
        "Price (Average)": rows.reduce((sum, row) => sum + row["Price (Average)"], 0),
        "Smallholder Farms (ESC)": rows.reduce((sum, row) => sum + row["Smallholder Farms (ESC)"], 0),
        "Medium-Scale Farms (EMS)": rows.reduce((sum, row) => sum + row["Medium-Scale Farms (EMS)"], 0),
        "Farmer Cooperatives (EFC)": rows.reduce((sum, row) => sum + row["Farmer Cooperatives (EFC)"], 0),
        "Contract Farming (ECF)": rows.reduce((sum, row) => sum + row["Contract Farming (ECF)"], 0),
        "Niche Farms (ENS)": rows.reduce((sum, row) => sum + row["Niche Farms (ENS)"], 0),
        "Agri-Entrepreneurs (EAE)": rows.reduce((sum, row) => sum + row["Agri-Entrepreneurs (EAE)"], 0),
        "Youth/Women-led (EYW)": rows.reduce((sum, row) => sum + row["Youth/Women-led (EYW)"], 0)
      };
      rows.push(totals);
    }
  
    return rows;
  };

  const handleDownload = async (format) => {
    const data = generateReportData();
    if (!data || data.length === 0) {
      console.error('No data available for export');
      return;
    }
  
    try {
      switch (format) {
        case 'pdf':
          // PDF generation code remains the same
          break;
  
        case 'csv':
          // Prepare CSV content with headers
          const headers = Object.keys(data[0]);
          let csvContent = headers.join(',') + '\n';
          
          // Add data rows
          data.forEach(row => {
            csvContent += headers.map(header => row[header]).join(',') + '\n';
          });
          
          // Add metadata
          csvContent += `\nGenerated on,${new Date().toLocaleString()}\n`;
          csvContent += `Country,${reportSelections.COUNTRY}\n`;
          csvContent += `Region,${reportSelections.REGION === "-" ? "All Regions" : reportSelections.REGION}\n`;
          
          // Create and download CSV file
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          saveAs(blob, `report_${reportSelections.COUNTRY}_${new Date().toISOString().slice(0,10)}.csv`);
          break;
  
        case 'excel':
          // Create a new workbook
          const wb = XLSX.utils.book_new();
          
          // Convert data to worksheet
          const ws = XLSX.utils.json_to_sheet(data);
          
          // Add metadata
          const metadata = [
            ["Generated on", new Date().toLocaleString()],
            ["Country", reportSelections.COUNTRY],
            ["Region", reportSelections.REGION === "-" ? "All Regions" : reportSelections.REGION],
            [],
            ...XLSX.utils.sheet_to_json(ws, { header: 1 })
          ];
          
          const wsWithMeta = XLSX.utils.aoa_to_sheet(metadata);
          
       // Update the column widths in the Excel export section
wsWithMeta["!cols"] = [
  { width: 20 }, // Region column wider
  { width: 15 }, // Agricultural Entrepreneurs
  { width: 15 }, // Land for Farming
  { width: 15 }, // Production Cost
  { width: 15 }, // Revenue
  { width: 15 }, // Inventory
  { width: 15 }, // Price (Average)
  { width: 15 }, // Smallholder Farms
  { width: 15 }, // Medium-Scale Farms
  { width: 15 }, // Farmer Cooperatives
  { width: 15 }, // Contract Farming
  { width: 15 }, // Niche Farms
  { width: 15 }, // Agri-Entrepreneurs
  { width: 15 }  // Youth/Women-led
];
          
          // Add bold style to header row (row 4 since we have 3 metadata rows and an empty row)
          for (let col = 0; col < Object.keys(data[0]).length; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: 4, c: col });
            if (!wsWithMeta[cellAddress]) continue;
            wsWithMeta[cellAddress].s = { font: { bold: true } };
          }
          
          XLSX.utils.book_append_sheet(wb, wsWithMeta, "Report");
          
          // Generate and download Excel file
          XLSX.writeFile(wb, `report_${reportSelections.COUNTRY}_${new Date().toISOString().slice(0,10)}.xlsx`);
          break;
  
        default:
          console.error('Unsupported format');
      }
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };


  const handleDropdownChange = (type, value) => {
    setDropdownSelections(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const [dropdownSelections, setDropdownSelections] = useState({
    user_type: "English", // Default to English
    user_purpose: "View reports",
    user_category: "All categories"
  });

  const [reportSelections, setReportSelections] = useState({
    COUNTRY: "-",
    REGION: "-",
    DISTRICT: "-",
    MUNICIPALITY: "-",
    SMMEs: "-",
    "VALUE CHAIN": "-",
    PROJECT: "-",
    "USER TYPE": "-"
  });

  const handleReportDropdownChange = (type, value) => {
    setReportSelections(prev => {
      const newSelections = {
        ...prev,
        [type]: value
      };
      
      // When country changes, reset dependent fields
      if (type === 'COUNTRY') {
        newSelections.REGION = "-";
        newSelections.DISTRICT = "-";
        newSelections.MUNICIPALITY = "-";
      }
      
      return newSelections;
    });
  };
  
  return (
    <div className="bg-[#FAFBFB]">
      <div className="md:pt-4 pt-1">
        <Header/>
        <div className="flex flex-col md:min-h-screen">
          <div className="flex flex-grow">
            <div>
              <AdminSideBar />
            </div>
            <div className="w-full">
              <div className="flex justify-between items-center px-4">
                <h2 className="md:text-[36px] text-[12px] capitalize text-[#152C24] font-bold">
                  Reports 
                </h2>
                <div className="md:w-[650px] md:mb-4 md:mt-6 mb-2">
                  <div className="relative">
                    <input
                      placeholder="Search"
                      type="text"
                      className="w-full md:h-[61px] h-[30px] text-[#979797] text-[10px] md:text-[16px] font-semibold md:p-3 pr-10 p-1 md:px-6 px-3 border border-[#C4C4C4] md:rounded-md rounded-full focus:outline-none focus:ring-2 focus:ring-green-600"
                    />
                    <img
                      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744490602/Search_k7vxkr.png"
                      alt="Search Icon"
                      className="w-[18px] h-[15px] md:h-[24px] md:w-[24px] absolute right-5 top-1/2 transform -translate-y-1/2"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center px-4">
                <div>
                  <h1 className="md:text-[16px] text-[6px] font-bold mb-2">ALL REPORTS</h1>
                  <button className="md:w-[165px] md:h-[50px] rounded-md bg-[#2E453D] border-[#CFD7D7] border-2 text-white font-normal p-1 text-center">
                    ALL REPORTS
                  </button>
                  <p className="md:text-[14px] font-normal mt-1">
                    Click to view all countries reports
                  </p>
                </div>
                
                <div className="flex items-center">
                  <Dropdown1
                    label="COUNTRY"
                    items={languageOptions}
                    type="FACILITATOR"
                    isProjectDropdown={false}
                    onChange={(type, value) => handleDropdownChange('user_type', value)}
                    className="border-r-0 rounded-r-none"
                  />
                </div>
              </div>
              <div>
                <ReportDropdown2
                  values={reportSelections}
                  onChange={handleReportDropdownChange}
                  smmesOptions={SMMEsOptions}
                />
              </div>
              <div className="flex">
              <div className="reports-container md:w-[600px]">
  <Reports ref={reportsRef} />
</div>
                <div className="ml-4 w-[400px] h-[500px] bg-white shadow-sm">
                  <div className="mb-4">
                    <h3 className="text-[20px] font-bold">Share Report</h3>
                    <p className="text-[16px] font-normal mt-1">Select Format</p>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Row 1 - PDF */}
                    <div 
                      className="flex items-center justify-between h-[50px] pb-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleDownload('pdf')}
                    >
                      <div className="flex items-center">
                        <img 
                          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1753223631/Order_csh5hk.png" 
                          alt="Format Icon" 
                          className="w-6 h-6 mr-2"
                        />
                        <span className="text-[20px]">PDF</span>
                      </div>
                      <span className="text-[20px] font-normal">Share</span>
                    </div>
                    
                    {/* Row 2 - CSV */}
                    <div 
                      className="flex items-center justify-between h-[50px] pb-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleDownload('csv')}
                    >
                      <div className="flex items-center">
                        <img 
                          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1753223631/Order_csh5hk.png" 
                          alt="Format Icon" 
                          className="w-6 h-6 mr-2"
                        />
                        <span className="text-[20px]">CSV</span>
                      </div>
                      <span className="text-[20px] font-normal">Share</span>
                    </div>
                    
                    {/* Row 3 - Excel */}
                    <div 
                      className="flex items-center justify-between h-[50px] pb-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleDownload('excel')}
                    >
                      <div className="flex items-center">
                        <img 
                          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1753223631/Order_csh5hk.png" 
                          alt="Format Icon" 
                          className="w-6 h-6 mr-2"
                        />
                        <span className="text-[20px]">Excel</span>
                      </div>
                      <span className="text-[20px] font-normal">Share</span>
                    </div>
                    
                    {/* Row 4 - Records Unavailable */}
                    <div className="flex items-center justify-between h-[50px] pb-2">
                      <div className="flex items-center">
                        <img 
                          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1753223631/Order_csh5hk.png" 
                          alt="Format Icon" 
                          className="w-6 h-6 mr-2"
                        />
                        <span className="text-[20px]">Records Unavailable</span>
                      </div>
                      <span className="text-[28px] font-normal text-gray-600 cursor-pointer">x</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-auto">
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboards;